import axios, { AxiosResponse } from "axios";
import { WebAPI, IFailCallbackParam, ChartRowDataArray } from "../constants";
import { getErrorStatus } from "./getErrorStatus";
import AuthService from "../../common/AuthService";
import { createRowData } from "./common";

interface IGetProfileResponceModel {
  /**
   * タイトル文字列の配列
   */
  titles: string[];
  /**
   * プロファイルデータの配列
   */
  rows: ChartRowDataArray;
  /**
   * ETAG
   */
  etag?: string;
}

function convertData(data: IGetProfileResponceModel): ChartRowDataArray {
  //cols
  /*var datalist: any[] = new Array(data.titles.length);
  data.titles.forEach((title, i) => {
    datalist[i] = new Array(data.rows.length + 1);
    datalist[i][0] = title;
  });
  data.rows.forEach((row: any[], i) => {
    row.forEach((pos: any, j) => {
      datalist[j][i + 1] = pos;
    });
  });*/

  // rows
  const datalist: ChartRowDataArray = new Array(data.rows.length + 1);
  datalist[0] = ["x", ...data.titles];
  data.rows.forEach((row, i) => {
    const idx = i + 1;
    datalist[idx] = [idx, ...row];
  });
  return datalist;
}

function AxiosGet(url: string, token?: string) {
  if (token) {
    return axios.get(url, {
      headers: {
        Authorization: "Bearer " + token,
      },
    });
  } else {
    return axios.get(url);
  }
}

export interface IProfileRow {
  /**
   * チャネル
   */
  channel: string;
  /**
   * チャートデータ
   */
  data: ChartRowDataArray;
  /**
   * データ数
   */
  datanum: number;
  /**
   * ETAG
   */
  etag: string;
}

interface ICallbackParams extends IFailCallbackParam {
  /**
   * WebAPIからプロファイルの取得が成功した場合に呼び出されます
   * @callback success
   * @param {IProfileRow} profile - プロファイル
   * @param {number} status - httpステータス
   */
  success: (profile: IProfileRow, status: number) => void;
}

interface GetProfileModel {
  /**
   * チャネル
   */
  channel: string;
  /**
   * データ間引き閾値
   */
  threshold?: number;
}

interface IAxiosInternalParams extends GetProfileModel, ICallbackParams {
  promise: Promise<AxiosResponse>;
}

function AxiosInternal(param: IAxiosInternalParams) {
  const threshold =
    typeof param.threshold === "undefined" ? 0 : param.threshold;
  param.promise
    .then((res) => {
      const { etag } = res.data;
      const data = convertData(res.data);
      param.success(
        {
          channel: param.channel,
          data: createRowData(data, threshold, false),
          datanum: data.length - 1,
          etag: etag ? etag : "",
        },
        res.status
      );
    })
    .catch((err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    });
}

export interface IGetProfileParams extends GetProfileModel, ICallbackParams {
  /**
   * 認証オブジェクト
   */
  authService?: AuthService;
  /**
   * プロジェクトID
   */
  projectid: string;
  /**
   * 401エラーでリトライする場合はtrue
   */
  isRetry401: boolean;
}

/**
 * プロファイルを取得します
 * @function getProfile
 * @param {IGetProfileParams} params - プロファイル取得パラメータ
 */
export function getProfile(params: IGetProfileParams) {
  const { authService, projectid, channel, threshold, isRetry401 } = params;
  const url = WebAPI.GET_PROFILE(projectid, channel);
  if (authService) {
    authService.getToken({
      success: (token) => {
        AxiosInternal({
          promise: AxiosGet(url, token),
          channel: channel,
          threshold: threshold,
          success: params.success,
          fail: !isRetry401
            ? params.fail
            : (id, status) => {
                if (status === 401) {
                  getProfileRetry401(authService, url, params, params);
                } else {
                  params.fail(id, status);
                }
              },
        });
      },
      fail: (err) => {
        const { id, status } = getErrorStatus(err);
        params.fail(id, status);
      },
    });
  } else {
    AxiosInternal({
      promise: AxiosGet(url),
      channel: channel,
      threshold: threshold,
      success: params.success,
      fail: params.fail,
    });
  }
}

/**
 * プロファイルを取得します
 * @function getProfileRetry401
 * @param {AuthService} authService - 認証オブジェクト
 * @param {string} url - リクエストURL
 * @param {GetProfileModel} model - プロファイル取得モデル
 * @param {ICallbackParams} param - コールバックパラメータ
 */
export function getProfileRetry401(
  authService: AuthService,
  url: string,
  model: GetProfileModel,
  param: ICallbackParams
) {
  authService.updateToken({
    success: (token) => {
      AxiosInternal({
        promise: AxiosGet(url, token),
        channel: model.channel,
        threshold: model.threshold,
        success: param.success,
        fail: param.fail,
      });
    },
    fail: (err) => {
      const { id, status } = getErrorStatus(err);
      param.fail(id, status);
    },
  });
}
